/* The searchable key derived from a lot number.
 *
 * Lot numbers are the document ids in `lots`, and Firestore can only match a
 * document id from its first character. That is fine for "CB_590" and useless
 * for "590" — which is the part people actually remember, because the bit in
 * front of it is not consistent: the same number appears as CB_590, SDS590,
 * SDS_590 and SDS 590 across 39 distinct prefixes.
 *
 * So the digits are stored on the document as `lotNoDigits`, where a range
 * query can reach them. See searchLots in src/firebase/firebase.utils.js for
 * the read side and scripts/backfill-lot-digits.js for the one-off pass over
 * lots written before this existed.
 *
 * Written as CommonJS on purpose: this is imported by the app *and* required
 * by a plain Node script, which cannot load an ES module. One copy means the
 * backfill and the app can never disagree about a lot's key — which they
 * would eventually, if this were duplicated.
 *
 * Unit tests: src/utils/lotSearchKeys.test.js
 */

// The field name lives here rather than as a literal at each use, so app,
// script and tests cannot drift apart on it.
const LOT_DIGITS_FIELD = "lotNoDigits";

/**
 * The first run of digits in a lot number.
 *
 *   "CB_590"    -> "590"      "SDS 590"   -> "590"
 *   "CB_590mix" -> "590"      "APRIL MIX" -> ""
 *
 * Three deliberate decisions:
 *
 *   - Lots with no digits store `""` rather than nothing. A stored empty
 *     string means "this lot has been backfilled and has no number", which
 *     `--verify` can tell apart from "never backfilled", and no real query can
 *     reach it because a search string is never empty.
 *
 *   - The FIRST run only. Three lots in the collection have a second one
 *     ("CB_155 S1", "CB_331 CB_332 Offload", "CB_523E2") and in every case the
 *     first run is the lot number. Those lots stay findable by id prefix, so
 *     an array field and array-contains would buy nothing.
 *
 *   - Leading zeros are kept verbatim, so a hypothetical "CB_0590" would not
 *     answer to "590". No such lot exists today; if one appears, canonicalise
 *     both sides here.
 */
const lotNoDigits = (lotNo) => {
  const match = String(lotNo || "").match(/[0-9]+/);
  return match ? match[0] : "";
};

/**
 * The digit search term for a typed query, or "" if the query is not purely
 * digits. This is the branch switch in searchLots: an empty result means the
 * query takes the document-id prefix path exactly as it always has.
 */
const lotDigitsQuery = (query) => {
  const trimmed = String(query || "").trim();
  return /^[0-9]+$/.test(trimmed) ? trimmed : "";
};

module.exports = { LOT_DIGITS_FIELD, lotNoDigits, lotDigitsQuery };
